// Sad style instruments (docs/AUDIO_DESIGN.md §12, style 'sad'): the felt piano of piano-instruments.js played softer and
// further away (a warbling, darker bus) with a slow string pad swelling under its long chords, a round sub, a hushed kit
// and a muted-trumpet lead that sighs (leans in from the scale step above, falls away at the end of long notes).
// Every node goes in the voice registry: persistent parts tagged `${tag}:p`, per-note voices `${tag}`.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';
import { createPiano } from './piano-instruments.js';

// Levels (tuning knobs for the mix balance against jazzhop's EP / bass / kit / lead).
export const SAD = { keys: 1.12, pad: 0.08, bass: 0.23, kit: 1.25, lead: 1.9 };

const mod12 = n => ((n % 12) + 12) % 12;
const SCALE = { minor: [0, 2, 3, 5, 7, 8, 10], major: [0, 2, 4, 5, 7, 9, 11] };
/** the key's scale note just above m (a semitone or a whole step): where a sigh leans from */
export const stepAbove = (m, key) => (SCALE[key.mode].includes(mod12(m + 1 - key.tonic)) ? m + 1 : m + 2);
const stopAt = (s, t) => { try { s.stop(t); } catch { /* not started or already stopped */ } };
/** delay swing (s) that bends the pitch ±c cents at f Hz: (2^(c/1200) − 1) / (2πf) (the mixer's wow formula) */
const wowSec = (c, f) => (Math.pow(2, c / 1200) - 1) / (2 * Math.PI * f);

/* ---------------- keys: the felt piano, far away, with a string pad under long chords ---------------- */
/**
 * createSadKeys(ac, out, send, plan, ctx) → { chord, stats, dispose }. plan.piano / plan.ep.lp: the piano (createPiano, its
 * felt LP) · plan.sad: { warble ¢, rate Hz, pad, padAtk τ, padRel τ, det ¢, padLp }.
 * Bus: piano + pad → warble (a DelayNode at 12 ms swept by sines at rate (.7 × warble ¢) and .19 × rate (.5 × warble ¢): a
 * slow drift on the keys only, over the tape's own wow) → SAD.keys → dry; the piano's reverb send ×1.5 (.42), the pad's .5.
 * Pad (string machine, 3 nodes a note): 2 saws ±det¢ (×.7–1.3) → amp SAD.pad·pad·v, attack τ padAtk (a 1.5–2.5 s swell),
 * release τ padRel (the final chord: the engine's .9 s). It plays under chords of ≥ 3 notes held ≥ 1.5 s: the voicing's
 * lowest 3 notes from MIDI 48 up, the left hand's root an octave up when it clears them by a minor 3rd. One voice per pitch:
 * a note starting where the same pitch ends ties (common tones and 2-bar chords never re-attack). Pad bus (mono: the reverb
 * gives it width): LP padLp (Q .5) → peaking 650 Hz +4 dB (an "oo") → the keys bus.
 * Voices: the pad keeps ≤ 4 (3 in lite), stealing the one released first (0.3 s fade); the piano ≤ 10 (7): 14 of ctx.cap 16,
 * 10 of 10 (a chord change holds 5 damping notes + 5 new; a steal takes a damping one first).
 */
export function createSadKeys(ac, out, send, plan, { registry, tag, cap = 16, t0 = ac.currentTime, log = null, r = Math.random, stepDur = 0.25 }) {
  const S = plan.sad, nodes = [], src = [];
  const N = x => (nodes.push(x), x);
  const dly = N(ac.createDelay(0.05)), outG = N(gain(ac, SAD.keys));
  dly.delayTime.value = 0.012;
  dly.connect(outG); outG.connect(out);
  const l1 = N(osc(ac, 'sine', S.rate)), g1 = N(gain(ac, wowSec(0.7 * S.warble, S.rate)));
  const l2 = N(osc(ac, 'sine', 0.19 * S.rate)), g2 = N(gain(ac, wowSec(0.5 * S.warble, 0.19 * S.rate)));
  l1.connect(g1); g1.connect(dly.delayTime); l2.connect(g2); g2.connect(dly.delayTime); src.push(l1, l2);
  const pSend = N(gain(ac, 1.5)), padSend = N(gain(ac, 0.5));
  pSend.connect(send); padSend.connect(send);
  const pLP = N(filter(ac, 'lowpass', S.padLp, 0.5)), vowel = N(filter(ac, 'peaking', 650, 1.1, 4));
  pLP.connect(vowel); vowel.connect(dly); vowel.connect(padSend);
  const bus = persistent(registry, tag, src, nodes, t0);
  const padCap = cap >= 16 ? 4 : 3;
  const piano = createPiano(ac, dly, pSend, plan, { registry, tag, cap: cap >= 16 ? 10 : cap - padCap, r, t0, log, stepDur });

  let pads = [], padMax = 0, padNotes = 0, padSteals = 0;            // { v, m, amp, off, end (−35 dB), rec }
  const padNote = (t, m, vel, dur, relTau) => {
    const off = t + Math.max(0.3, dur), A = SAD.pad * S.pad * vel;
    pads = pads.filter(x => x.end > t);
    const y = pads.find(x => x.m === m);
    if (y) {                                          // the same pitch: tie on where it ends, else swell back from its release
      const g = y.amp.gain, tie = t < y.off + 0.15 && y.off > ac.currentTime + 0.02, o2 = tie ? Math.max(off, y.off) : off;
      if (tie) { g.cancelScheduledValues(y.off); g.setTargetAtTime(A, y.off, 1); } else { hold(g, t); g.setTargetAtTime(A, t, S.padAtk); }
      g.setTargetAtTime(0, o2, relTau);
      const end = o2 + 5 * relTau;
      for (const o of y.v.src) stopAt(o, end);
      y.v.end = end; y.off = o2; y.end = o2 + 4 * relTau;
      if (y.rec) y.rec[1] = y.end;
      return;
    }
    if (pads.length >= padCap) {
      let k = 0;
      for (let i = 1; i < pads.length; i++) if (pads[i].off < pads[k].off) k = i;
      const old = pads.splice(k, 1)[0];
      registry.stop(old.v, t, 0.1);
      old.end = t; if (old.rec) old.rec[1] = t;
      padSteals++;
    }
    const f = mtof(m), d = S.det * (0.7 + 0.6 * r()), end = off + 5 * relTau;
    const o1 = osc(ac, 'sawtooth', f), o2 = osc(ac, 'sawtooth', f), amp = gain(ac, 0);
    o1.detune.value = d; o2.detune.value = -d;
    o1.connect(amp); o2.connect(amp); amp.connect(pLP);
    amp.gain.setValueAtTime(0, t); amp.gain.setTargetAtTime(A, t, S.padAtk); amp.gain.setTargetAtTime(0, off, relTau);
    for (const o of [o1, o2]) { o.start(t); o.stop(end); }
    const x = { v: registry.add({ src: [o1, o2], all: [o1, o2, amp], start: t, end, tag, out: amp }), m, amp, off, end: off + 4 * relTau, rec: log ? [t, off + 4 * relTau] : null };
    if (x.rec) log.push(x.rec);
    pads.push(x);
    padNotes++;
    padMax = Math.max(padMax, pads.length);
  };

  return {
    chord(t, midis, vel, dur, strum = 0.03, rel = 0.08) {
      piano.chord(t, midis, vel, dur, strum, rel);
      if (midis.length < 3 || dur < 1.5) return;      // the pad swells only under long, full chords
      const up = midis.filter(m => m >= 48 && m <= 67), lh = midis[0];
      const notes = lh < 48 && (!up.length || up[0] - (lh + 12) >= 3) ? [lh + 12, ...up] : up;
      for (const m of notes.slice(0, 3)) padNote(t + 0.02, m, vel, dur, rel >= 0.5 ? rel : S.padRel);
    },
    stats: () => { const p = piano.stats(); return { ...p, maxActive: p.maxActive + padMax, steals: p.steals + padSteals, notes: p.notes + padNotes, active: p.active + pads.length }; },
    dispose(t) {
      const a = Math.max(ac.currentTime, t - 1.5);   // the pad's tail and the warble fade before the bus is freed
      hold(outG.gain, a); outG.gain.setTargetAtTime(0, a, 0.3);
      piano.dispose(t); bus.dispose(t);
    },
  };
}

/* ---------------- sub: one oscillator (a sine + .3 of a triangle + .14 of the octave) → LP 520 Hz, a slow soft gate ---------------- */
/** Partials 1 · .14 · .027 · 0 · .01 (a sine, the octave, a triangle's 3rd and 5th at .3), 3 nodes. Amplitude SAD.bass·v with
 *  τ 45 ms (no thump), easing to .72 of it (τ 1.4 s), release τ .25 s; a note starting while the last still sounds glides to
 *  its pitch (τ 35 ms), so a walking line is legato. The octave and the 3rd/5th carry it on a phone speaker. */
export function createSadSub(ac, out, { registry, tag, t0 = ac.currentTime }) {
  const o = osc(ac, 'sine', 55), lp = filter(ac, 'lowpass', 520, 0.5), amp = gain(ac, 0);
  o.setPeriodicWave(ac.createPeriodicWave(new Float32Array(6), Float32Array.from([0, 1.24, 0.14, -0.027, 0, 0.01]), { disableNormalization: true }));
  o.connect(lp); lp.connect(amp); amp.connect(out);
  const p = persistent(registry, tag, [o], [o, lp, amp], t0);
  let lastOff = -Infinity;
  return {
    note(t, midi, vel, dur) {
      const f = mtof(midi), off = t + Math.max(0.3, dur), A = SAD.bass * vel, legato = t < lastOff + 0.08;
      hold(o.frequency, t);
      if (legato) o.frequency.setTargetAtTime(f, t, 0.035); else o.frequency.setValueAtTime(f, t);
      hold(amp.gain, t);
      amp.gain.setTargetAtTime(A, t, 0.045); amp.gain.setTargetAtTime(0.72 * A, t + 0.15, 1.4); amp.gain.setTargetAtTime(0, off, 0.25);
      lastOff = off;
    },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- hushed kit: persistent voices sharing one noise source ---------------- */
/**
 * kit: { kickF0, kickF1, kickDecay, snareBP, snareDecay, hatHP, hatClosed, lp }. Everything through the bus LP (kit.lp,
 * 2.6–4.8 kHz: muffled); reverb send .35 (brush, rim), the kick and hat dry.
 * Kick: sine kickF0 → kickF1 (τ 40 ms), .5v, attack τ 3 ms, decay kickDecay; a felt "tup": noise → BP 300 Hz, .25v, τ 12 ms.
 * Brush: noise → BP snareBP (Q .55), .55v, in τ 4 ms, out snareDecay (ghosts τ 60 ms).
 * Rim (a muted cross-stick): sine 400 + triangle 1100 (.5) → BP 750 Hz (Q .8), .16v, τ 14 ms. A mono kit (no panners).
 * Hat: noise → HP hatHP (3.6–4.8 kHz, Q .5, just over the bus LP: a dull tick), .16v, closed τ hatClosed, open .16 s.
 */
export function createHushKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, r = Math.random }) {
  const L = SAD.kit, nodes = [];
  const N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5)), sendG = N(gain(ac, 0.35));
  bus.connect(out); sendG.connect(send);
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kBP = N(filter(ac, 'bandpass', 300, 0.9)), kTup = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kBP); kBP.connect(kTup); kTup.connect(bus);
  const sBP = N(filter(ac, 'bandpass', kit.snareBP, 0.55)), sNoise = N(gain(ac, 0));
  src.connect(sBP); sBP.connect(sNoise); sNoise.connect(bus); sNoise.connect(sendG);
  const r1 = N(osc(ac, 'sine', 400)), r2 = N(osc(ac, 'triangle', 1100)), rg2 = N(gain(ac, 0.5));
  const rBP = N(filter(ac, 'bandpass', 750, 0.8)), rAmp = N(gain(ac, 0));
  r1.connect(rBP); r2.connect(rg2); rg2.connect(rBP); rBP.connect(rAmp); rAmp.connect(bus); rAmp.connect(sendG);
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.5)), hAmp = N(gain(ac, 0));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(bus);
  const p = persistent(registry, tag, [src, kOsc, r1, r2], nodes, t0, r() * 3);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.04);
      hit(kAmp.gain, 0.5 * v * L, t, 0.003, 0.012, kit.kickDecay);
      hit(kTup.gain, 0.25 * v * L, t, 0.002, 0.004, 0.012);
    },
    snare(t, v, ghost = false) {
      hit(sNoise.gain, 0.55 * v * L, t, 0.004, 0.014, ghost ? 0.06 : kit.snareDecay);
    },
    hat(t, v, open = false) { hit(hAmp.gain, 0.16 * v * L, t, open ? 0.006 : 0.002, 0.004, open ? 0.16 : kit.hatClosed); },
    rim(t, v) { hit(rAmp.gain, 0.16 * v * L, t, 0.001, 0.003, 0.014); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- lead: a muted trumpet, mono ---------------- */
/**
 * plan.lead: { pan } · plan.sad: { lean, vib }. One oscillator on a brass series (partial n at 1/n^1.4, the fundamental 1.6:
 * a sawtooth, rounder, with a sine under it) → low-pass (Q .8) → peaking 1.25 kHz +5 dB (Q 1.4: the cup mute's nasal ring)
 * → amp; breath: noise → BP 1.6 kHz (Q .8) → the amp, 1.1 on the tongued onset (τ 60 ms) → .35 under the note. The cutoff
 * blooms with the breath: from .5× to 1.35× its sustain (650 + 1.6f)·(.75 + .5v) Hz (≤ 3.2 kHz) in ~40 ms, settling in
 * 250 ms. Amplitude .2·v·SAD.lead (×(1 − .015 per semitone over MIDI 76): the top eased), attack τ 28 ms, easing to .86
 * (τ .45 s), release τ .14 s; a slurred note (the engine's glide) glides τ 40 ms without re-tonguing. Vibrato 4.6 Hz, ±vib ¢
 * from .35 s (τ .25 s). Sighs: a note ≥ .9 s leans in from the key's scale step above with p lean and falls to its pitch after
 * min(.4 s, .3 × dur) (τ 70 ms), easing to .8 of its level; every note ≥ .5 s sags −40¢ as it fades (τ .12 s).
 * Pan plan.lead.pan; a quarter-note echo (fb .3, wet .26) behind it; reverb .5.
 */
export function createMuteHorn(ac, out, send, plan, { registry, tag, stepDur, t0 = ac.currentTime, r = Math.random }) {
  const S = plan.sad, K = plan.key, pan0 = plan.lead.pan || 0, nodes = [];
  const N = x => (nodes.push(x), x);
  const horn = N(osc(ac, 'sine', 440)), lp = N(filter(ac, 'lowpass', 1200, 0.8)), cup = N(filter(ac, 'peaking', 1250, 1.4, 5));
  horn.setPeriodicWave(ac.createPeriodicWave(new Float32Array(17), Float32Array.from({ length: 17 }, (_, n) => (n === 1 ? 1.6 : n ? Math.pow(n, -1.4) : 0))));
  const nSrc = N(ac.createBufferSource()), nBP = N(filter(ac, 'bandpass', 1600, 0.8)), breath = N(gain(ac, 0));
  const amp = N(gain(ac, 0)), pan = N(panner(ac, pan0)), sendG = N(gain(ac, 0.5)), vib = N(osc(ac, 'sine', 4.6)), vibG = N(gain(ac, 0));
  nSrc.buffer = noise(ac, 'white'); nSrc.loop = true;
  horn.connect(lp); lp.connect(cup); cup.connect(amp);
  nSrc.connect(nBP); nBP.connect(breath); breath.connect(amp);
  vib.connect(vibG); vibG.connect(horn.detune);
  amp.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const dly = N(ac.createDelay(2)), fb = N(gain(ac, 0.3)), wet = N(gain(ac, 0.26));
  dly.delayTime.value = Math.min(1.9, 4 * stepDur);
  amp.connect(dly); dly.connect(fb); fb.connect(dly); dly.connect(wet); wet.connect(pan);
  const p = persistent(registry, tag, [horn, nSrc, vib], nodes, t0, r() * 2);
  const FALL = Math.pow(2, -40 / 1200);
  let lastOff = -Infinity;
  return {
    note(t, midi, vel, dur, glide = false) {
      const f = mtof(midi), v = clamp(vel, 0.05, 1), d = Math.max(0.08, dur), off = t + d, A = 0.2 * v * SAD.lead * (1 - 0.015 * clamp(midi - 76, 0, 12));
      const slur = glide && t < lastOff + 0.3, lean = !slur && d >= 0.9 && r() < S.lean, tr = t + Math.min(0.4, 0.3 * d);
      const fc = Math.min(3200, (650 + 1.6 * f) * (0.75 + 0.5 * v)), f0 = lean ? mtof(stepAbove(midi, K)) : f;
      const q = horn.frequency, c = lp.frequency;
      hold(q, t);
      if (slur) q.setTargetAtTime(f0, t, 0.04); else q.setValueAtTime(f0, t);
      if (lean) q.setTargetAtTime(f, tr, 0.07);
      if (d >= 0.5) q.setTargetAtTime(f * FALL, off, 0.12);
      hold(c, t);
      if (slur) c.setTargetAtTime(fc, t, 0.08);
      else { c.setTargetAtTime(0.5 * fc, t, 0.004); c.setTargetAtTime(1.35 * fc, t + 0.012, 0.035); c.setTargetAtTime(fc, t + 0.09, 0.25); }
      const g = amp.gain;
      hold(g, t);
      g.setTargetAtTime(slur ? 0.9 * A : A, t, slur ? 0.04 : 0.028);
      g.setTargetAtTime(0.86 * A, t + 0.12, 0.45);
      if (lean) g.setTargetAtTime(0.8 * A, tr, 0.12);
      g.setTargetAtTime(0, off, 0.14);
      hold(breath.gain, t);
      breath.gain.setTargetAtTime(slur ? 0.35 : 1.1, t, 0.006); breath.gain.setTargetAtTime(0.35, t + 0.03, 0.06);
      hold(vibG.gain, t);
      vibG.gain.setTargetAtTime(0, t, 0.03);
      if (d > 0.6) vibG.gain.setTargetAtTime(S.vib, t + 0.35, 0.25);
      lastOff = off;
    },
    dispose(t) { p.dispose(t); },
  };
}
